import { Autocomplete, TextField } from '@mui/material';
import { AutocompleteProps } from '@mui/material/Autocomplete/Autocomplete';
import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import useWsAutocomplete from '../hooks/useWsAutocomplete';

interface WsAsyncAutocompleteProps<T>
  extends Omit<
    AutocompleteProps<T, boolean | undefined, boolean | undefined, boolean | undefined>,
    'renderInput' | 'options'
  > {
  name: string;
  getOptions: (inputValue: string) => Promise<T[]>;
  labelField?: string;
  creatable?: boolean;
  onCreate?: (inputValue: string, setValue) => void;
  multiple?: boolean;
  placeholder?: string;
  required?: boolean;
  changed?(value: string | null | T | (string | T)[]): void;
  getOptionLabel?(option: T): string;
  disable?: boolean;
}

export const WsAsyncAutocomplete = <T,>({
  name,
  getOptions,
  labelField = 'label',
  onCreate,
  creatable = false,
  multiple = false,
  required = false,
  changed = () => {},
  getOptionLabel,
  disable = false,
  ...props
}: WsAsyncAutocompleteProps<T>) => {
  const methods = useFormContext();
  const autocompleteProps = useWsAutocomplete<T>(getOptionLabel ?? labelField, creatable);
  const [options, setOptions] = useState<T[]>([]);

  const onInputChange = async (inputValue: string) => {
    setOptions([]);
    if (inputValue !== '') {
      const result = await getOptions(inputValue);
      setOptions(result);
    }
  };

  const { placeholder, ...rest } = props;

  return (
    <Controller
      control={methods.control}
      name={name}
      render={({ field: { onChange, value } }) => (
        <Autocomplete
          value={value}
          size="small"
          multiple={multiple}
          selectOnFocus
          handleHomeEndKeys
          options={options}
          disabled={disable}
          getOptionLabel={autocompleteProps.getOptionLabel}
          onChange={(event, item) => {
            if (creatable && item && item['inputValue']) {
              onCreate &&
                onCreate(item['inputValue'], (createdValue) => {
                  setOptions([createdValue]);
                  onChange(createdValue);
                });
            }
            onChange(item);
            changed(item);
          }}
          onInputChange={(event, value) => onInputChange(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              {...(placeholder && { label: placeholder })}
              {...(required && {
                inputProps: {
                  ...params.inputProps,
                  required: multiple ? value?.length === 0 : !value,
                },
              })}
            />
          )}
          {...rest}
        />
      )}
    />
  );
};
